import React from 'react'
import { Box, Button, GlobalStyles, Typography } from '@mui/material'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import BackLink from '../system/BackLink'
import { PILL_RADIUS } from '../../agent/components/surface'
import { readerLines } from '../../utils/guided'
import type { GuidePoint, GuideReader } from '../../types/gotypes'

export interface GuideAgendaPrintProps {
  title: string
  intro?: string
  goal?: string
  readersNote?: string
  /** The guide's reviewers, named under "Who reads your answers". */
  reviewers: GuideReader[]
  points: GuidePoint[]
  /** Opens the browser's print dialog (window.print), where Save as PDF makes the PDF. */
  onPrint: () => void
  /** A way back, drawn above the page and never printed. */
  back?: { label: string; onClick: () => void }
}

/**
 * The agenda as a printable page, so people can read the questions before
 * they start. The PDF is made by the browser's own Save as PDF: no new
 * dependency. Everything but the agenda is left off the printout.
 */
const GuideAgendaPrint: React.FC<GuideAgendaPrintProps> = ({ title, intro, goal, readersNote, reviewers, points, onPrint, back }) => (
  <Box sx={{ height: '100%', overflow: 'auto', backgroundColor: 'background.default', '@media print': { height: 'auto', overflow: 'visible', backgroundColor: '#fff' } }}>
    {/* The app's frame scrolls inside a fixed-height main; on paper every page must flow. */}
    <GlobalStyles
      styles={{
        '@media print': {
          '@page': { margin: '18mm' },
          'html, body, #root, #main-content': { height: 'auto !important', overflow: 'visible !important', background: '#fff' },
        },
      }}
    />
    <Box sx={{ maxWidth: 760, mx: 'auto', px: 3, pt: 2, display: 'flex', alignItems: 'center', gap: 2, '@media print': { display: 'none' } }}>
      {back && <BackLink label={back.label} onClick={back.onClick} />}
      <Box sx={{ flex: 1 }} />
      <Button variant="contained" color="info" disableElevation startIcon={<PrintOutlinedIcon />} onClick={onPrint} sx={{ borderRadius: PILL_RADIUS }}>
        Print or save as PDF
      </Button>
    </Box>
    <Box
      component="article"
      data-testid="guide-agenda-print"
      sx={{
        maxWidth: 760, mx: 'auto', px: 3, py: 3, color: 'text.primary',
        '@media print': { maxWidth: 'none', p: 0, color: '#000' },
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary' }}>
        Guided conversation · Agenda
      </Typography>
      <Typography component="h1" sx={{ fontSize: '1.7rem', fontWeight: 700, mt: 0.5 }}>{title}</Typography>
      {intro && <Typography sx={{ mt: 2, fontSize: 14.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{intro}</Typography>}
      {goal && (
        <>
          <Typography component="h2" sx={{ mt: 3, fontSize: 15, fontWeight: 700 }}>What this is for</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{goal}</Typography>
        </>
      )}
      <Typography component="h2" sx={{ mt: 3, fontSize: 15, fontWeight: 700 }}>Who reads your answers</Typography>
      {[...readerLines(reviewers), ...(readersNote ? [readersNote] : [])].map((line) => (
        <Typography key={line} sx={{ mt: 0.5, fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{line}</Typography>
      ))}
      <Typography component="h2" sx={{ mt: 3, fontSize: 15, fontWeight: 700 }}>The questions</Typography>
      <Box component="ol" sx={{ mt: 1, pl: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {points.map((p) => (
          <Box component="li" key={p.id} data-testid="guide-agenda-point" sx={{ breakInside: 'avoid' }}>
            <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700 }}>{p.title}</Typography>
            {p.question && <Typography sx={{ mt: 0.25, fontSize: 14, lineHeight: 1.55 }}>{p.question}</Typography>}
            {p.covered_when.length > 0 && (
              <>
                <Typography sx={{ mt: 0.75, fontSize: 13, fontWeight: 600, color: 'text.secondary', '@media print': { color: '#333' } }}>
                  Useful to cover
                </Typography>
                <Box component="ul" sx={{ mt: 0.25, mb: 0, pl: 2.5 }}>
                  {p.covered_when.map((c) => (
                    <Box component="li" key={c} sx={{ fontSize: 13, lineHeight: 1.5 }}>{c}</Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
)

export default GuideAgendaPrint
